import { endOfDay, format, parse, startOfDay } from "date-fns";

/**
 * Inclusive calendar-day range for table filters (`yyyy-MM-dd`).
 * Either bound may be omitted (open-ended).
 */
export type TDateRangeFilter = {
    from?: string;
    to?: string;
};

export function normalizeDateRangeFilter(range: {
    from: string;
    to: string;
}): TDateRangeFilter | undefined {
    const from = range.from.trim() || undefined;
    const to = range.to.trim() || undefined;
    if (!from && !to) return undefined;
    return { from, to };
}

/** Coerce legacy `YYYY-MM` month keys to full dates for filter bounds. */
function coerceStartBound(s: string): string {
    const t = s.trim();
    if (!t) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    if (/^\d{4}-\d{2}$/.test(t)) return `${t}-01`;
    return t;
}

function coerceEndBound(s: string): string {
    const t = s.trim();
    if (!t) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
    if (/^\d{4}-\d{2}$/.test(t)) {
        const parts = t.split("-").map(Number);
        return format(new Date(parts[0]!, parts[1]!, 0), "yyyy-MM-dd");
    }
    return t;
}

export function parseColumnDateRangeFilter(v: unknown): { from: string; to: string } {
    if (v && typeof v === "object" && ("from" in v || "to" in v)) {
        const o = v as { from?: string; to?: string };
        return {
            from: coerceStartBound(o.from ?? ""),
            to: coerceEndBound(o.to ?? ""),
        };
    }
    if (typeof v === "string" && v.trim()) {
        const t = v.trim();
        return { from: coerceStartBound(t), to: "" };
    }
    return { from: "", to: "" };
}

export function isDateRangeFilterActive(v: unknown): boolean {
    if (v === undefined || v === null) return false;
    const { from, to } = parseColumnDateRangeFilter(v);
    return Boolean(from || to);
}

/** Normalise a cell value to local start-of-day for range checks. */
export function parseCellToFilterDay(value: string | null | undefined): Date | null {
    if (value == null || value === "") return null;
    const s = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        const d = parse(s.slice(0, 10), "yyyy-MM-dd", new Date());
        return Number.isNaN(d.getTime()) ? null : startOfDay(d);
    }
    if (/^\d{4}-\d{2}$/.test(s)) {
        const d = parse(`${s}-01`, "yyyy-MM-dd", new Date());
        return Number.isNaN(d.getTime()) ? null : startOfDay(d);
    }
    const t = Date.parse(s);
    if (Number.isNaN(t)) return null;
    return startOfDay(new Date(t));
}

export function matchesDateRangeFilter(
    cellDay: Date | null,
    filter: TDateRangeFilter | undefined,
): boolean {
    if (!filter || (!filter.from && !filter.to)) return true;
    if (!cellDay) return false;
    let fromD: Date | null = null;
    let toD: Date | null = null;
    if (filter.from) {
        const p = parse(filter.from, "yyyy-MM-dd", new Date());
        if (Number.isNaN(p.getTime())) return true;
        fromD = startOfDay(p);
    }
    if (filter.to) {
        const p = parse(filter.to, "yyyy-MM-dd", new Date());
        if (Number.isNaN(p.getTime())) return true;
        toD = endOfDay(p);
    }
    if (fromD && cellDay < fromD) return false;
    if (toD && cellDay > toD) return false;
    return true;
}
